import {
  MissionAssessmentEffectCostDtoCurrencyEnum,
  MissionAssessmentEffectCreateDto,
  MissionAssessmentEffectTimeUnit,
  MissionAssessmentFieldName,
} from '@@types/api.types';
import { Modal } from '@components/core/modal.component';
import { EffectSelectOption } from '@components/shared/effect-definition.component';
import { FormSuggestion, useFormSuggestion } from '@components/shared/form-suggestion.component';
import { CurrencyInput } from '@components/ui/currency-input';
import { useAppForm } from '@components/ui/form/form';
import { FormField, FormLabel } from '@components/ui/form/shared.component';
import { config } from '@config';
import maDefinitions from '@data/ma-definitions.json';
import { errorToast } from '@hooks/toast.hook';
import { useGetDomains } from '@queries/domain.query';
import { useGetEffects } from '@queries/effect.query';
import { useGetTargetSystems } from '@queries/target-system.query';
import { useGetUses } from '@queries/use.query';
import { missionAssessmentEffectCreateSchema } from '@schemas/mission-assessment.schema';
import { PHASES_OF_OPERATIONS_LABELS } from '@utils/constants';
import { createFormInvalidHandler, createSuggestionTextApplier } from '@utils/form.util';
import { mapToSelectOptions } from '@utils/select.util';
import React, { useMemo, useCallback } from 'react';
import { MissionAssessmentGenerateButton } from './mission-assessment-generate-button.component';

type MissionAssessmentFormEffectsModalProps = {
  effectToEdit?: MissionAssessmentEffectCreateDto | null;
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (effectData: MissionAssessmentEffectCreateDto) => void;
  technologyId?: string;
};

const phaseOptions = Array.from({ length: 6 }, (_, i) => ({
  label: `${i} - ${PHASES_OF_OPERATIONS_LABELS[i]}`,
  value: i.toString(),
}));

const generatePhaseOptions = (
  selectedMin?: number | null,
  selectedMax?: number | null,
  isMaxOptions = false,
): { value: string; label: string }[] => {
  return phaseOptions.filter((option) => {
    const value = parseInt(option.value, 10);
    if (isMaxOptions && selectedMin !== undefined && selectedMin !== null && value < selectedMin) return false;
    if (!isMaxOptions && selectedMax !== undefined && selectedMax !== null && value > selectedMax) return false;
    return true;
  });
};

const getDefaultValues = (effectToEdit?: MissionAssessmentEffectCreateDto | null): MissionAssessmentEffectCreateDto => {
  if (effectToEdit) return effectToEdit;

  return {
    data: {
      cost: {
        amount: 0,
        currency: MissionAssessmentEffectCostDtoCurrencyEnum.USD,
      },
      costToReplace: {
        amount: 0,
        currency: MissionAssessmentEffectCostDtoCurrencyEnum.USD,
      },
      description: '',
      measureOfImpact: '',
      measuresOfPerformance: '',
      personnelRequired: undefined,
      minPhase: undefined,
      maxPhase: undefined,
      timeToDeploy: undefined,
      timeToReplace: undefined,
      unit: '',
    },
    domainId: '',
    effectId: '',
    secondaryEffects: [],
    targetSystems: [],
    useId: '',
  } as MissionAssessmentEffectCreateDto;
};

const isAIEnabled = config.ai.enabled;

export const MissionAssessmentFormEffectsModal: React.FC<MissionAssessmentFormEffectsModalProps> = (props) => {
  const { isOpen, onClose, onSubmit, effectToEdit, technologyId } = props;

  const { data: availableUses, isLoading: isLoadingUses } = useGetUses();
  const { data: availableDomains, isLoading: isLoadingDomains } = useGetDomains();
  const { data: availableEffects, isLoading: isLoadingEffects } = useGetEffects();
  const { data: availableTargetSystems, isLoading: isLoadingTargetSystems } = useGetTargetSystems();

  const useOptions = useMemo(() => mapToSelectOptions(availableUses ?? []), [availableUses]);
  const domainOptions = useMemo(() => mapToSelectOptions(availableDomains ?? []), [availableDomains]);

  const timeUnitOptions = useMemo(() => {
    return Object.values(MissionAssessmentEffectTimeUnit).map((unit) => ({
      label: unit.charAt(0).toUpperCase() + unit.slice(1).toLowerCase(),
      value: unit,
    }));
  }, []);

  const {
    handleSuggestionStream,
    acceptSuggestion,
    rejectSuggestion,
    getSuggestion,
    getSuggestionModel,
    getIsStreaming,
    handleMetadata,
    handleStreamingStart,
    handleStreamingEnd,
  } = useFormSuggestion<MissionAssessmentFieldName>();

  const form = useAppForm({
    defaultValues: getDefaultValues(effectToEdit),
    canSubmitWhenInvalid: true,
    onSubmit: async ({ value }) => {
      try {
        const validationResult = missionAssessmentEffectCreateSchema.safeParse(value);

        if (!validationResult.success) {
          const message = validationResult.error.issues[0].message;
          errorToast({
            description: message || 'Please fill out all required fields correctly.',
          });
          return;
        }

        onSubmit(validationResult.data);
      } catch (error) {
        console.error('❌ Error in form submission:', error);
        errorToast({ description: 'An error occurred while submitting the form.' });
      }
    },
    validators: {
      onSubmit: missionAssessmentEffectCreateSchema,
    },
    onSubmitInvalid: createFormInvalidHandler(),
  });

  const applySuggestionText = useMemo(
    () =>
      createSuggestionTextApplier<MissionAssessmentFieldName>({
        [MissionAssessmentFieldName.EFFECT_DESCRIPTION]: (text) => form.setFieldValue('data.description', text),
        [MissionAssessmentFieldName.EFFECT_MEASURE_OF_IMPACT]: (text) =>
          form.setFieldValue('data.measureOfImpact', text),
        [MissionAssessmentFieldName.EFFECT_MEASURES_OF_PERFORMANCE]: (text) =>
          form.setFieldValue('data.measuresOfPerformance', text),
      }),
    [form],
  );

  const handleAcceptSuggestion = useCallback(
    (fieldName: MissionAssessmentFieldName) => {
      acceptSuggestion(fieldName, (text: string) => applySuggestionText(fieldName, text));
    },
    [acceptSuggestion, applySuggestionText],
  );

  const renderGenerateButton = useCallback(
    (params: {
      fieldName: MissionAssessmentFieldName;
      onStream: (text: string) => void;
      onStreamingEnd: () => void;
      onStreamingStart: () => void;
    }) => {
      const { fieldName, onStream, onStreamingEnd, onStreamingStart } = params;

      if (!technologyId || !isAIEnabled) return null;

      return (
        <form.Subscribe
          selector={(state) => ({
            effectId: state.values.effectId,
            secondaryEffectIds: state.values.secondaryEffects,
          })}
        >
          {(effectContext) => (
            <MissionAssessmentGenerateButton
              effectContext={effectContext}
              fieldName={fieldName}
              onAccept={(text) => applySuggestionText(fieldName, text)}
              onMetadata={(metadata) =>
                handleMetadata({
                  key: fieldName,
                  metadata,
                })
              }
              onStream={onStream}
              onStreamingEnd={onStreamingEnd}
              onStreamingStart={onStreamingStart}
              technologyId={technologyId}
            />
          )}
        </form.Subscribe>
      );
    },
    [applySuggestionText, form, handleMetadata, technologyId],
  );

  return (
    <Modal
      centered
      footer={{
        leftButton: {
          onClick: onClose,
          children: 'Cancel',
          variant: 'secondary',
        },
        rightButton: {
          onClick: () => {
            // Clear all field validation errors to force fresh validation
            form.validateAllFields('submit');
            form.handleSubmit();
          },
          children: effectToEdit ? 'Save Changes' : 'Add Effect',
        },
      }}
      header={{ title: effectToEdit ? 'Edit Effect' : 'Add Effect' }}
      isOpen={isOpen}
      onClose={onClose}
      width="800px"
    >
      <form.AppForm>
        <div className="space-y-4 text-sm">
          <form.AppField
            name="effectId"
            validators={{
              onChange: missionAssessmentEffectCreateSchema.shape.effectId,
            }}
          >
            {(field) => (
              <field.SelectPopoverField
                disabled={isLoadingEffects}
                emptyMessage="No effects found."
                formatValue={(option) => option?.label || 'Select Effect'}
                label="Effect"
                options={availableEffects || []}
                placeholder="Select Effect"
                required
                renderOption={(option) => <EffectSelectOption option={option} />}
                searchPlaceholder="Search effects..."
                tooltip={maDefinitions?.effect}
              />
            )}
          </form.AppField>

          <form.AppField
            name="useId"
            validators={{
              onChange: missionAssessmentEffectCreateSchema.shape.useId,
            }}
          >
            {(field) => (
              <field.SelectPopoverField
                disabled={isLoadingUses}
                emptyMessage="No uses found."
                formatValue={(option) => option?.label || 'Select Use'}
                label="Use"
                options={useOptions}
                placeholder="Select Use"
                required
                searchPlaceholder="Search uses..."
              />
            )}
          </form.AppField>

          <form.AppField
            name="domainId"
            validators={{
              onChange: missionAssessmentEffectCreateSchema.shape.domainId,
            }}
          >
            {(field) => (
              <field.SelectPopoverField
                disabled={isLoadingDomains}
                emptyMessage="No domains found."
                formatValue={(option) => option?.label || 'Select Domain'}
                label="Domain"
                options={domainOptions}
                placeholder="Select Domain"
                required
                searchPlaceholder="Search domains..."
              />
            )}
          </form.AppField>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-1">
            <div className="col-span-1 md:col-span-2">
              <FormLabel tooltip={maDefinitions?.phaseOfOperations}>Phases of Operation</FormLabel>
            </div>

            <form.Subscribe selector={(state) => state.values.data}>
              {(data) => {
                const minPhase = data.minPhase;
                const maxPhase = data.maxPhase;

                return (
                  <>
                    <form.AppField
                      name="data.minPhase"
                      validators={{
                        onChangeListenTo: ['data.maxPhase'],
                      }}
                    >
                      {(field) => {
                        return (
                          <field.SelectField
                            clearable
                            options={generatePhaseOptions(undefined, maxPhase)}
                            placeholder="Select Min Phase"
                          />
                        );
                      }}
                    </form.AppField>

                    <form.AppField
                      name="data.maxPhase"
                      validators={{
                        onChangeListenTo: ['data.minPhase'],
                      }}
                    >
                      {(field) => {
                        return (
                          <field.SelectField
                            clearable
                            options={generatePhaseOptions(minPhase, undefined, true)}
                            placeholder="Select Max Phase"
                          />
                        );
                      }}
                    </form.AppField>
                  </>
                );
              }}
            </form.Subscribe>
          </div>

          <form.AppField name="data.unit">
            {(field) => (
              <field.TextField
                label="Unit"
                placeholder="eg: per kill, per flight hour"
                tooltip={maDefinitions?.unitOfEffect}
              />
            )}
          </form.AppField>

          <div className="grid grid-cols-2 gap-4">
            <form.AppField name="data.cost">
              {(field) => (
                <FormField>
                  <FormLabel>Cost</FormLabel>
                  <CurrencyInput<MissionAssessmentEffectCostDtoCurrencyEnum>
                    currency={MissionAssessmentEffectCostDtoCurrencyEnum.USD}
                    emptyValue="zero"
                    onChange={field.handleChange}
                    placeholder="0.00"
                    value={field.state.value}
                  />
                </FormField>
              )}
            </form.AppField>

            <form.AppField name="data.costToReplace">
              {(field) => (
                <FormField>
                  <FormLabel tooltip={maDefinitions?.costToReplace}>Cost to Replace</FormLabel>
                  <CurrencyInput<MissionAssessmentEffectCostDtoCurrencyEnum>
                    currency={MissionAssessmentEffectCostDtoCurrencyEnum.USD}
                    emptyValue="zero"
                    onChange={field.handleChange}
                    placeholder="0.00"
                    value={field.state.value}
                  />
                </FormField>
              )}
            </form.AppField>
          </div>

          <FormField>
            <FormLabel tooltip={maDefinitions?.timeToDeploy}>Time to Deploy</FormLabel>
            <div className="grid grid-cols-2 gap-4">
              <form.AppField
                name="data.timeToDeploy.value"
                validators={{
                  onChangeListenTo: ['data.timeToDeploy.unit'],
                }}
              >
                {(field) => (
                  <field.TextField
                    min="0"
                    step="1"
                    inputMode="numeric"
                    placeholder="eg: 1"
                    type="number"
                  />
                )}
              </form.AppField>
              <form.AppField
                name="data.timeToDeploy.unit"
                validators={{
                  onChangeListenTo: ['data.timeToDeploy.value'],
                }}
              >
                {(field) => {
                  const timeUnit = field.state.value;
                  return (
                    <field.SelectField
                      clearable
                      options={timeUnitOptions}
                      placeholder="Select Unit"
                      value={timeUnit ?? ''}
                    />
                  );
                }}
              </form.AppField>
            </div>
          </FormField>

          <FormField>
            <FormLabel tooltip={maDefinitions?.timeToReplace}>Time to Replace</FormLabel>
            <div className="grid grid-cols-2 gap-4">
              <form.AppField
                name="data.timeToReplace.value"
                validators={{
                  onChangeListenTo: ['data.timeToReplace.unit'],
                }}
              >
                {(field) => (
                  <field.TextField
                    min="0"
                    step="1"
                    inputMode="numeric"
                    placeholder="eg: 1"
                    type="number"
                  />
                )}
              </form.AppField>

              <form.AppField
                name="data.timeToReplace.unit"
                validators={{
                  onChangeListenTo: ['data.timeToReplace.value'],
                }}
              >
                {(field) => {
                  const timeUnit = field.state.value;
                  return (
                    <field.SelectField
                      clearable
                      options={timeUnitOptions}
                      placeholder="Select Unit"
                      value={timeUnit ?? ''}
                    />
                  );
                }}
              </form.AppField>
            </div>
          </FormField>

          <form.AppField name="data.personnelRequired">
            {(field) => (
              <field.TextField
                label="Number of Personnel Required"
                min="0"
                step="1"
                inputMode="numeric"
                placeholder="eg: 2 (engineers)"
                type="number"
              />
            )}
          </form.AppField>

          <form.AppField name="data.description">
            {(field) => (
              <div className="space-y-2">
                <div className="flex gap-1 items-center">
                  <label
                    className="text-sm font-medium text-gray-700"
                    title={maDefinitions?.characteristicsAndMeasures}
                  >
                    Characteristics & Measures
                  </label>
                </div>
                <field.TextAreaField
                  generateButton={renderGenerateButton({
                    fieldName: MissionAssessmentFieldName.EFFECT_DESCRIPTION,
                    onStream: (text) =>
                      handleSuggestionStream({ key: MissionAssessmentFieldName.EFFECT_DESCRIPTION, text }),
                    onStreamingEnd: () => handleStreamingEnd(MissionAssessmentFieldName.EFFECT_DESCRIPTION),
                    onStreamingStart: () => handleStreamingStart(MissionAssessmentFieldName.EFFECT_DESCRIPTION),
                  })}
                  placeholder="Enter characteristics including physical, functional, cognitive, environmental, temporal."
                  rows={3}
                />
                <FormSuggestion
                  model={getSuggestionModel(MissionAssessmentFieldName.EFFECT_DESCRIPTION)}
                  onAccept={() => handleAcceptSuggestion(MissionAssessmentFieldName.EFFECT_DESCRIPTION)}
                  onReject={() => rejectSuggestion(MissionAssessmentFieldName.EFFECT_DESCRIPTION)}
                  rejectButtonProps={{ variant: 'destructive' }}
                  suggestion={getSuggestion(MissionAssessmentFieldName.EFFECT_DESCRIPTION)}
                  isStreaming={getIsStreaming(MissionAssessmentFieldName.EFFECT_DESCRIPTION)}
                />
              </div>
            )}
          </form.AppField>

          <form.AppField name="data.measuresOfPerformance">
            {(field) => (
              <div className="space-y-2">
                <FormLabel tooltip={maDefinitions?.measureOfPerformance}>Measures of Performance</FormLabel>
                <field.TextAreaField
                  generateButton={renderGenerateButton({
                    fieldName: MissionAssessmentFieldName.EFFECT_MEASURES_OF_PERFORMANCE,
                    onStream: (text) =>
                      handleSuggestionStream({
                        key: MissionAssessmentFieldName.EFFECT_MEASURES_OF_PERFORMANCE,
                        text,
                      }),
                    onStreamingEnd: () => handleStreamingEnd(MissionAssessmentFieldName.EFFECT_MEASURES_OF_PERFORMANCE),
                    onStreamingStart: () =>
                      handleStreamingStart(MissionAssessmentFieldName.EFFECT_MEASURES_OF_PERFORMANCE),
                  })}
                  placeholder="Describe the measures of performance."
                  rows={3}
                />
                <FormSuggestion
                  model={getSuggestionModel(MissionAssessmentFieldName.EFFECT_MEASURES_OF_PERFORMANCE)}
                  onAccept={() => handleAcceptSuggestion(MissionAssessmentFieldName.EFFECT_MEASURES_OF_PERFORMANCE)}
                  onReject={() => rejectSuggestion(MissionAssessmentFieldName.EFFECT_MEASURES_OF_PERFORMANCE)}
                  rejectButtonProps={{ variant: 'destructive' }}
                  suggestion={getSuggestion(MissionAssessmentFieldName.EFFECT_MEASURES_OF_PERFORMANCE)}
                  isStreaming={getIsStreaming(MissionAssessmentFieldName.EFFECT_MEASURES_OF_PERFORMANCE)}
                />
              </div>
            )}
          </form.AppField>

          <form.AppField name="data.measureOfImpact">
            {(field) => (
              <div className="space-y-2">
                <div className="flex gap-1 items-center">
                  <FormLabel tooltip={maDefinitions?.measureOfImpactEffectiveness}>
                    Measures of Impact/Effectiveness
                  </FormLabel>
                </div>

                <field.TextAreaField
                  generateButton={renderGenerateButton({
                    fieldName: MissionAssessmentFieldName.EFFECT_MEASURE_OF_IMPACT,
                    onStream: (text) =>
                      handleSuggestionStream({
                        key: MissionAssessmentFieldName.EFFECT_MEASURE_OF_IMPACT,
                        text,
                      }),
                    onStreamingEnd: () => handleStreamingEnd(MissionAssessmentFieldName.EFFECT_MEASURE_OF_IMPACT),
                    onStreamingStart: () => handleStreamingStart(MissionAssessmentFieldName.EFFECT_MEASURE_OF_IMPACT),
                  })}
                  placeholder="Describe the measures of impact/effectiveness."
                  rows={3}
                />

                <FormSuggestion
                  model={getSuggestionModel(MissionAssessmentFieldName.EFFECT_MEASURE_OF_IMPACT)}
                  onAccept={() => handleAcceptSuggestion(MissionAssessmentFieldName.EFFECT_MEASURE_OF_IMPACT)}
                  onReject={() => rejectSuggestion(MissionAssessmentFieldName.EFFECT_MEASURE_OF_IMPACT)}
                  rejectButtonProps={{ variant: 'destructive' }}
                  suggestion={getSuggestion(MissionAssessmentFieldName.EFFECT_MEASURE_OF_IMPACT)}
                  isStreaming={getIsStreaming(MissionAssessmentFieldName.EFFECT_MEASURE_OF_IMPACT)}
                />
              </div>
            )}
          </form.AppField>

          <form.AppField name="secondaryEffects">
            {(field) => (
              <field.SelectMultipleField
                disabled={isLoadingEffects}
                label="Secondary Effects"
                options={availableEffects || []}
                placeholder="Type or select secondary effects..."
                renderOption={(option) => <EffectSelectOption option={option} />}
                tooltip={maDefinitions?.secondaryEffect}
              />
            )}
          </form.AppField>

          <form.AppField name="targetSystems">
            {(field) => (
              <field.SelectMultipleField
                disabled={isLoadingTargetSystems}
                label="Target Systems"
                options={availableTargetSystems || []}
                placeholder="Type or select target systems..."
                tooltip={maDefinitions?.targetSystem}
              />
            )}
          </form.AppField>
        </div>
      </form.AppForm>
    </Modal>
  );
};
